import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, KineticText } from "@/components/Reveal";

const IMG_STUDY =
    "https://images.unsplash.com/photo-1618544976420-1f213fcf2052?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const VALUES = [
    {
        num: "01",
        title: "Personal attention",
        body: "Every student is known by name, by strength and by the exact chapter they are stuck on.",
    },
    {
        num: "02",
        title: "Discipline over shortcuts",
        body: "Regular tests, fixed routines and honest feedback — habits that outlast any single exam.",
    },
    {
        num: "03",
        title: "Concepts before marks",
        body: "When fundamentals are strong, marks follow. We teach for understanding first.",
    },
    {
        num: "04",
        title: "Parents as partners",
        body: "Progress is shared openly with families so support continues beyond the classroom.",
    },
];

const About = () => (
    <main className="pt-36 sm:pt-44 pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <Reveal>
                <p className="eyebrow">About Us</p>
            </Reveal>
            <h1 className="mt-7 f-display text-[clamp(2.5rem,7vw,5.5rem)] font-black uppercase leading-[0.98] tracking-tighter text-white">
                <KineticText
                    delay={0.2}
                    lines={[
                        "We make excellence",
                        <span key="a">
                            {" "}
                            <span className="f-serif normal-case font-medium text-[#FF5500]">
                                a habit.
                            </span>
                        </span>,
                    ]}
                />
            </h1>

            <div className="mt-14 grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr]">
                <Reveal>
                    <div className="space-y-6 text-base sm:text-lg leading-relaxed text-slate-400">
                        <p>
                            <span className="f-display font-bold text-white">
                                Vivek Coaching Centre
                            </span>{" "}
                            was founded on a simple belief — that every student
                            can go further with the right guidance. Not louder
                            lectures or bigger batches, but closer attention:
                            a teacher who knows where you struggle and refuses
                            to let you stay there.
                        </p>
                        <p>
                            We coach school students from Classes 6 to 12 across
                            all subjects, prepare aspirants for JEE and NEET
                            foundations, and train candidates for SSC, Banking
                            and other competitive examinations. Our approach is
                            deliberately old-school in discipline and modern in
                            method — concepts first, practice always, tests
                            every week, doubts cleared daily.
                        </p>
                        <p>
                            What we care about is not a lucky result but a
                            repeatable one: the calm, prepared confidence of a
                            student who has done the work. That is what we
                            build, batch after batch.
                        </p>
                    </div>
                    <Link to="/contact" data-testid="about-cta" className="btn-primary mt-9">
                        Visit or call us
                        <ArrowUpRight size={17} />
                    </Link>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="img-frame img-spotlight h-80 sm:h-[460px]">
                        <img
                            src={IMG_STUDY}
                            alt="Students collaborating in a study session"
                        />
                    </div>
                    <div className="glass mt-6 rounded-2xl p-6">
                        <p className="f-serif text-xl text-white">
                            &ldquo;Strong concepts, steady routine, honest
                            feedback.&rdquo;
                        </p>
                        <p className="mt-3 f-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                            The Vivek method, in one line
                        </p>
                    </div>
                </Reveal>
            </div>

            <div className="mt-28">
                <Reveal>
                    <p className="eyebrow">What We Stand For</p>
                    <h2 className="mt-6 f-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                        Four commitments, kept daily.
                    </h2>
                </Reveal>
                <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2">
                    {VALUES.map((v, i) => (
                        <Reveal key={i} delay={i * 0.07} className="bg-[#0A0E16] p-8">
                            <span className="chapter-num text-5xl">{v.num}</span>
                            <h3 className="mt-5 f-display text-lg font-bold text-white">
                                {v.title}
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">
                                {v.body}
                            </p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </main>
);

export default About;
